import type {
    AccountSummary,
    HolisticSummaryCategory,
    Provider,
} from '@fruitful/types';
import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import {
    CheckIcon,
    FullScreenSpinner,
    Icon,
    ScreenContainer,
    useToastController,
    View,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { findAccountByGroupedAccountId } from '../../../common/utils/holisticUtils';
import useUpdateManualAccount from '../../../holistic/hooks/useUpdateManualAccount';
import { captureException } from '../../../logger/Logger';
import useGetHolisticSummary from '../../hooks/useGetHolisticSummary';
import useToggleAccountVisibility from '../../hooks/useToggleAccountVisibility';
import { holisticSummaryAtom } from '../../state/HolisticAtoms';
import type { IManualAccount, UpdateAccount } from '../../types/ManualAccount';
import ConfirmDeleteSheet from './ConfirmDeleteSheet';
import ManualAccountEditor from './ManualAccountEditor';

export const ManualAccount = ({
    accountCategory,
    accountId,
}: {
    accountCategory: HolisticSummaryCategory;
    accountId: string;
}) => {
    const user = useAuthedUser();
    const toast = useToastController();

    const [account, setAccount] = useState<AccountSummary | undefined>(
        undefined,
    );

    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

    const { mutate: updateAccount, isPending: isLoading } =
        useUpdateManualAccount();

    const holisticSummary = useAtomValue(holisticSummaryAtom);

    const {
        mutateAsync: toggleAccountVisibility,
        isPending: isLoadingToggleVisibility,
    } = useToggleAccountVisibility();

    const {
        refetch: refetchHolisticSummary,
        isLoading: isLoadingHolisticSummary,
    } = useGetHolisticSummary(!holisticSummary?.[accountCategory]);

    useEffect(() => {
        const accounts = holisticSummary?.[accountCategory]?.accounts;
        const account = findAccountByGroupedAccountId(
            accountCategory,
            accounts ?? [],
            accountId,
        );

        if (!isLoadingHolisticSummary && accounts) {
            if (account) {
                setAccount({
                    ...account,
                    category: account.category as HolisticSummaryCategory,
                    type: account.type,
                });
            } else {
                router.back();
            }
        }
    }, [
        accountCategory,
        holisticSummary?.[accountCategory]?.accounts,
        accountId,
    ]);

    const onUpdateAccount = (account: Partial<IManualAccount>) => {
        updateAccount(
            {
                body: {
                    id: account.id ?? '',
                    userId: user.id,
                    membershipId: user.membership?.id ?? '',
                    amount: account.amount,
                    institutionName: account.institutionName,
                    holisticCategory: account.holisticCategory?.toUpperCase(),
                    type: account.type,
                    name: account.name,
                } satisfies UpdateAccount,
            },
            {
                onSuccess: (response: Partial<IManualAccount>) => {
                    void refetchHolisticSummary();
                    toast.show(`Updated`, {
                        icon: (
                            <View
                                backgroundColor="$green1000"
                                padding={16}
                                borderRadius={12}
                            >
                                <Icon
                                    icon={CheckIcon}
                                    size={20}
                                    color="$white"
                                />
                            </View>
                        ),
                        description: `${response.name ?? response.institutionName ?? 'Account'} updated`,
                    });
                    if (router.canGoBack()) {
                        router.back();
                    } else {
                        router.navigate('/');
                    }
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                },
            },
        );
    };

    const onSubmitDeleteAccount = async (
        accountId: string,
        provider: Provider,
    ) => {
        try {
            await toggleAccountVisibility({
                body: {
                    accountId,
                    provider,
                    isHidden: true,
                },
            });

            void refetchHolisticSummary();

            toast.show(`Deleted`, {
                icon: (
                    <View
                        backgroundColor="$green1000"
                        padding={16}
                        borderRadius={12}
                    >
                        <Icon icon={CheckIcon} size={20} color="$white" />
                    </View>
                ),
                description: `${account?.name} has been removed from your accounts`,
            });

            router.back();
        } catch (e) {
            // Still refresh in case this call partially worked
            void refetchHolisticSummary();

            captureException(e);
            toast.show('Error deleting account', {
                duration: 2000,
            });
        }
    };

    if (isLoading || account == null || isLoadingToggleVisibility) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer title="Account details" hasBackButton>
            <ManualAccountEditor
                canDelete={true}
                deleteAction={() => {
                    setShowDeleteConfirm(true);
                }}
                account={{
                    ...account,
                    amount: account.currentBalanceCents,
                    holisticCategory: account.category,
                    type: account.type,
                }}
                submitAction={onUpdateAccount}
                submitLabel="Save"
            />
            <ConfirmDeleteSheet
                isOpen={showDeleteConfirm}
                account={account}
                onSubmitDeleteAccount={onSubmitDeleteAccount}
                onClose={() => {
                    setShowDeleteConfirm(false);
                }}
            />
        </ScreenContainer>
    );
};
